import {Component, inject} from '@angular/core';
import {FormControl, FormGroup, Validators} from '@angular/forms';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {BehaviorSubject, combineLatest, tap} from 'rxjs';
import {TimeAppSuppliesService} from '../services/time-app-supplies.service';
import {MessageBoxService} from '../../../services/message-box.service';
import {first, startWith} from 'rxjs/operators';
import {ActivatedRoute, Router} from '@angular/router';
import {SupplyEntry, SupplyEntryParams} from '../../../models/swiss-service.models';
import {v4 as uuidv4} from 'uuid';
import {ZahlenFormatPipe} from '../../../pipes/zahlen-format.pipe';
import {formatCurrency} from '@angular/common';
import {MatDialog} from '@angular/material/dialog';
import {ServiceAppModels} from '../../../models/serviceapp-models';
import {DeviceDetectorService} from 'ngx-device-detector';
import {TimeAppWorkSupplyProductsDialogComponent} from '../time-app-work-supply-products/time-app-work-supply-products-dialog.component';
import {DataService} from '../../services/data.service';
import {UserAuthenticationService} from '../../../services/user-authentication.service';
import Produkt = ServiceAppModels.Produkt;

@Component({
    selector: 'app-time-app-work-supply-entry',
    templateUrl: './time-app-work-supply-entry.component.html',
    styleUrl: './time-app-work-supply-entry.component.scss'
})
export class TimeAppWorkSupplyEntryComponent {
    protected supplyForm: FormGroup;
    protected calcMode: any = new BehaviorSubject('');
    protected isMobile: BehaviorSubject<boolean> = new BehaviorSubject<any>(false);
    private calcPipe = inject(ZahlenFormatPipe);

    constructor(private userAuth: UserAuthenticationService, private dialog: MatDialog, private route: ActivatedRoute, private supplyService: TimeAppSuppliesService, private msgService: MessageBoxService, private router: Router, private deviceDetection: DeviceDetectorService, private dataService: DataService) {
        this.isMobile.next(this.deviceDetection.isMobile(navigator.userAgent));
        this.supplyForm = new FormGroup({
            rowID: new FormControl(),
            deleted: new FormControl(false, Validators.required),
            uploaded: new FormControl(false, Validators.required),
            objectID: new FormControl(),
            date: new FormControl<Date>(this.dataService.getChosenDate(), Validators.required),
            articleNumber: new FormControl('', Validators.required),
            articleDescription: new FormControl(''),
            quantity: new FormControl(1, [Validators.required, Validators.min(0)]),
            unit: new FormControl('Stk.'),
            pricePerUnit: new FormControl(0),
            priceTotal: new FormControl({value: '0', disabled: true}),
            length: new FormControl(0),
            width: new FormControl(0),
            thickness: new FormControl(0),
            amount: new FormControl({value: 0, disabled: true}),
            objectName: new FormControl(),
            productType: new FormControl(3)
        });

        this.supplyForm.controls.unit.valueChanges.pipe(
            takeUntilDestroyed(),
            tap(val => {
                this.resetRequiredFields();
                switch ((val || '').toLocaleLowerCase()) {
                    case 'm':
                    case 'm1':
                    case 'l1':
                    case 'lm':
                    case 'rk1':
                        this.supplyForm.controls['length'].setValidators(Validators.required);
                        this.calcMode.next('m');
                        break;
                    case 'm2':
                        this.supplyForm.controls['length'].setValidators(Validators.required);
                        this.supplyForm.controls['width'].setValidators(Validators.required);
                        this.calcMode.next('m²');
                        break;
                    case 'm3':
                        this.supplyForm.controls['length'].setValidators(Validators.required);
                        this.supplyForm.controls['width'].setValidators(Validators.required);
                        this.supplyForm.controls['thickness'].setValidators(Validators.required);
                        this.calcMode.next('m³');
                        break;
                    default:
                        this.calcMode.next('');
                }
            })
        ).subscribe();

        combineLatest({
            calcMode: this.calcMode.pipe(startWith('')),
            quantity: this.supplyForm.controls.quantity.valueChanges.pipe(startWith(0)),
            length: this.supplyForm.controls.length.valueChanges.pipe(startWith(0)),
            width: this.supplyForm.controls.width.valueChanges.pipe(startWith(0)),
            thickness: this.supplyForm.controls.thickness.valueChanges.pipe(startWith(0)),
        }).pipe(
            takeUntilDestroyed(),
            tap(val => {
                switch (val.calcMode) {
                    case 'm':
                        this.supplyForm.controls.amount.setValue(this.calcPipe.round((val.length || 1) * (val.quantity || 1) / 1000, 3));
                        break;
                    case 'm²':
                        this.supplyForm.controls.amount.setValue(this.calcPipe.round((val.length || 1) * (val.width || 1) * (val.quantity || 1) / 1000000, 3));
                        break;
                    case 'm³':
                        this.supplyForm.controls.amount.setValue(this.calcPipe.round((val.length || 1) * (val.width || 1) * (val.thickness || 1) * (val.quantity || 1) / 1000000000, 3));
                        break;
                    default:
                        this.supplyForm.controls.amount.setValue(val.quantity);
                        break;
                }
            })
        ).subscribe();

        combineLatest({
            pricePerUnit: this.supplyForm.controls.pricePerUnit.valueChanges.pipe(startWith(1)),
            amount: this.supplyForm.controls.amount.valueChanges.pipe(startWith(1)),
            quantity: this.supplyForm.controls.quantity.valueChanges.pipe(startWith(1)),
            calcMode: this.calcMode.pipe(startWith('')),
        }).pipe(
            takeUntilDestroyed(),
            tap(val => {
                let price = (val.quantity || 0) * (val.pricePerUnit || 0);
                if (val.calcMode) {
                    price = (val.amount || 0) * (val.pricePerUnit || 0);
                }
                this.supplyForm.controls.priceTotal.setValue(formatCurrency(price, 'de-CH', 'CHF', '1.2-2').replace(',', '.'));
            })
        ).subscribe();

        this.route.params.pipe(
            takeUntilDestroyed(),
            tap(async params => {
                const supplyEntry = await this.supplyService.getSupplyEntry(params.supplyEntryID);
                if (supplyEntry) {
                    this.supplyForm.setValue({
                        rowID: supplyEntry.rowID,
                        articleNumber: supplyEntry.articleNumber,
                        articleDescription: supplyEntry.articleDescription,
                        quantity: supplyEntry.quantity,
                        unit: supplyEntry.unit,
                        pricePerUnit: supplyEntry.pricePerUnit,
                        priceTotal: supplyEntry.priceTotal,
                        length: supplyEntry.length,
                        width: supplyEntry.width,
                        thickness: supplyEntry.thickness,
                        amount: supplyEntry.amount,
                        objectID: supplyEntry.objectID,
                        deleted: supplyEntry.deleted,
                        uploaded: supplyEntry.uploaded,
                        date: supplyEntry.date,
                        objectName: supplyEntry.objectName,
                        productType: supplyEntry.productType
                    });
                } else {
                    const currentOrder = this.supplyService.getCurrentOrderDetails();
                    this.supplyForm.patchValue({
                        rowID: uuidv4(),
                        objectID: currentOrder.isPseudoOrder ? null : currentOrder.identifier,
                        objectName: currentOrder.isPseudoOrder ? currentOrder.identifier : null,
                        productType: 3
                    });
                }
            })
        ).subscribe();
    }

    protected async openProductSelect() {
        this.dialog.open(TimeAppWorkSupplyProductsDialogComponent, {
            disableClose: true
        }).afterClosed().subscribe((product) => {
            if (product) {
                this.setProduct(product);
            }
        });
    }

    protected setProduct(product: Produkt | null) {
        if (!product) {
            this.enableFields();
            this.supplyForm.patchValue({
                productType: 3
            });
            return;
        }

        this.supplyForm.patchValue({
            articleNumber: product.productNo,
            articleDescription: product.shortText,
            unit: product.stockUnit,
            pricePerUnit: product.salePrice,
            productType: 1
        });

        this.supplyForm.controls['articleNumber'].disable();
        this.supplyForm.controls['articleDescription'].disable();
        this.supplyForm.controls['pricePerUnit'].disable();
        this.supplyForm.controls['unit'].disable();
    }

    protected async saveEntry() {
        if (!this.supplyForm.valid) {
            this.msgService.showErrorByLanguageKey(
                'MAIN_APP_TIME_PLUS.SUPPLY_ENTRY.ERRORS.invalidForm',
                'MAIN_APP_TIME_PLUS.SUPPLY_ENTRY.ERRORS.error'
            );
            return;
        }
        const params: SupplyEntryParams = this.supplyForm.getRawValue();
        params.mandator = this.userAuth.getCurrentMandant().mandatorID;
        params.employeeNr = this.userAuth.getCurrentAuthenticatedUser()!.employeeID;

        const supplyEntry: SupplyEntry = new SupplyEntry(params);

        (await this.supplyService.addOrUpdateEntry(supplyEntry)).pipe(first()).subscribe(res => {
            let hasError = false;
            switch (res.action) {
                case 'Updated':
                    this.msgService.showSuccessByLanguageKey(
                        'MAIN_APP_TIME_PLUS.SUPPLY_ENTRY.MESSAGES.addedEntry.text',
                        'MAIN_APP_TIME_PLUS.SUPPLY_ENTRY.MESSAGES.addedEntry.title'
                    );
                    break;
                case 'Added':
                    this.msgService.showSuccessByLanguageKey(
                        'MAIN_APP_TIME_PLUS.SUPPLY_ENTRY.MESSAGES.newEntry.text',
                        'MAIN_APP_TIME_PLUS.SUPPLY_ENTRY.MESSAGES.newEntry.title'
                    );
                    break;
                default:
                    hasError = true;
                    this.msgService.showError('FEHLER', 'FEHLER');
            }
            if (!hasError) {
                this.router.navigate(['../..'], {relativeTo: this.route});
            }
        });
    }

    private resetRequiredFields() {
        this.supplyForm.controls['length'].removeValidators(Validators.required);
        this.supplyForm.controls['width'].removeValidators(Validators.required);
        this.supplyForm.controls['thickness'].removeValidators(Validators.required);
    }

    private enableFields() {
        this.supplyForm.controls['articleNumber'].enable();
        this.supplyForm.controls['articleDescription'].enable();
        this.supplyForm.controls['pricePerUnit'].enable();
        this.supplyForm.controls['unit'].enable();
    }
}
